import type { ShapeCommittedUndoMeta } from '@/lib/shapes/types/shape-committed-undo-meta'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'

import { canvasMinForSingleShapePanel } from '@/lib/shapes/shape-panel-bounds'
import type { PositionField } from '../types/panel-fields'
import { multiSelectionCanvasUnionAabb } from './selection-bounds'

/** Ruler origin the panel's X/Y are shown relative to (gutter for X, top of content for Y). */
export interface PanelPageOrigin {
  useSecondPageOrigin: boolean
  rightPageOriginX: number
  rightPageOriginY: number
}

const EPS_POS = 1e-4

/** Canvas coordinate → the value the X/Y field shows. */
export function toDisplayPosition(
  canvasValue: number,
  field: PositionField,
  origin: PanelPageOrigin
): number {
  return origin.useSecondPageOrigin
    ? canvasValue - (field === 'x' ? origin.rightPageOriginX : origin.rightPageOriginY)
    : canvasValue
}

/** Value typed into the X/Y field → canvas coordinate. */
export function toCanvasPosition(
  displayValue: number,
  field: PositionField,
  origin: PanelPageOrigin
): number {
  return origin.useSecondPageOrigin
    ? displayValue + (field === 'x' ? origin.rightPageOriginX : origin.rightPageOriginY)
    : displayValue
}

/** Displayed X/Y to restore after blank or invalid panel input. */
export function positionRevertPoints(params: {
  field: PositionField
  isMultiSelect: boolean
  activeObjects: ShapeData[]
  activeObject: ShapeData | null
  mgr: ShapeManagerRef | null | undefined
  origin: PanelPageOrigin
}): number {
  const { field, isMultiSelect, activeObjects, activeObject, mgr, origin } = params

  if (isMultiSelect) {
    if (activeObjects.length === 0) return 0
    const union = multiSelectionCanvasUnionAabb(activeObjects)

    return toDisplayPosition(field === 'x' ? union.minX : union.minY, field, origin)
  }

  if (activeObject && mgr) {
    const { minX, minY } = canvasMinForSingleShapePanel(activeObject)

    return toDisplayPosition(field === 'x' ? minX : minY, field, origin)
  }

  return 0
}

/**
 * Undo metadata for a single-shape X/Y commit: only the edited axis when just that axis moved,
 * both axes when anything else moved, none when nothing moved.
 */
export function buildPanelMoveUndoMeta(
  field: PositionField,
  beforePos: { x: number; y: number },
  fresh: ShapeData | undefined
): ShapeCommittedUndoMeta | undefined {
  if (!fresh) return undefined
  const movedX = Math.abs(beforePos.x - fresh.x) > EPS_POS
  const movedY = Math.abs(beforePos.y - fresh.y) > EPS_POS
  const stayedX = Math.abs(beforePos.x - fresh.x) <= EPS_POS
  const stayedY = Math.abs(beforePos.y - fresh.y) <= EPS_POS

  if (field === 'x' && movedX && stayedY) {
    return { before: { x: beforePos.x }, after: { x: fresh.x } }
  }

  if (field === 'y' && movedY && stayedX) {
    return { before: { y: beforePos.y }, after: { y: fresh.y } }
  }

  if (movedX || movedY) {
    return { before: { x: beforePos.x, y: beforePos.y }, after: { x: fresh.x, y: fresh.y } }
  }

  return undefined
}
